/**
 * QuickCalculator Component v3 - Multi-OSD Support
 *
 * Enhanced "30-second" calculator for the landing page.
 * Provides tariff recommendations based on 4 inputs:
 * 0. Electricity distributor (OSD) selection
 * 1. Monthly electricity bill (PLN)
 * 2. Main usage time (day/night/even)
 * 3. Special features (heat pump, solar, EV, weekend, flexible) - multi-select
 *
 * Features:
 * - Supports all 4 Polish OSD: TAURON, PGE, ENEA, ENERGA
 * - Recommends tariffs available for selected OSD
 * - Shows alternative recommendation with condition
 * - Balanced recommendations when close
 *
 * Links to full calculator for detailed analysis.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  Title,
  Text,
  Slider,
  SegmentedControl,
  Stack,
  Group,
  Button,
  Card,
  ThemeIcon,
  Badge,
  Divider,
  Progress,
  Box,
  Checkbox,
  Alert,
  Select,
  Tooltip,
} from '@mantine/core';
import {
  IconSun,
  IconMoon,
  IconClock,
  IconArrowRight,
  IconCheck,
  IconFlame,
  IconSolarPanel,
  IconCar,
  IconCalendarWeek,
  IconBolt,
  IconInfoCircle,
  IconMapPin,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useOSD } from '../../../../contexts/OSDContext';
import { useOSDPricing, TariffPricing } from '../../../../hooks/useOSDPricing';
import { OSDCode, OSD_METADATA, OSD_CODES } from '../../../../utils/osdMetadata';
import { BUTTON_LABELS } from '../../../../constants/uiLabels';

// Types
type UsagePattern = 'day' | 'night' | 'even';
type SpecialFeature = 'heatPump' | 'solar' | 'ev' | 'weekend' | 'flexible';
type TariffType = 'G11' | 'G12' | 'G12as' | 'G12w' | 'G13' | 'G13s';

interface QuickResult {
  recommendedTariff: TariffType;
  alternativeTariff?: TariffType;
  alternativeCondition?: string;
  currentCost: number;
  recommendedCost: number;
  alternativeCost?: number;
  savings: number;
  savingsPercent: number;
  alternativeSavings?: number;
  alternativeSavingsPercent?: number;
}

/**
 * Get pricing data for the selected OSD
 * Converts TariffPricing to the format expected by calculation functions
 */
function getOSDPrices(getPricing: () => { tariffs: Record<string, TariffPricing> }) {
  const pricing = getPricing();
  return {
    G11: { flat: pricing.tariffs.G11?.flat ?? 1.10 },
    G12: { day: pricing.tariffs.G12?.day ?? 1.20, night: pricing.tariffs.G12?.night ?? 0.65 },
    G12as: { day: pricing.tariffs.G12as?.day ?? 1.15, night: pricing.tariffs.G12as?.night ?? 0.55 },
    G12w: { weekday: pricing.tariffs.G12w?.weekday ?? 1.15, weekend: pricing.tariffs.G12w?.weekend ?? 0.60 },
    G13: { peak: pricing.tariffs.G13?.peak ?? 1.35, offpeak: pricing.tariffs.G13?.offpeak ?? 0.70, night: pricing.tariffs.G13?.night ?? 0.55 },
    G13s: { solar: pricing.tariffs.G13s?.solar ?? 0.55, other: pricing.tariffs.G13s?.other ?? 1.05 },
  };
}

// Usage pattern distributions
const PATTERN_DISTRIBUTIONS = {
  day: { dayRatio: 0.70, nightRatio: 0.30 },
  even: { dayRatio: 0.50, nightRatio: 0.50 },
  night: { dayRatio: 0.30, nightRatio: 0.70 }
};

// Weekend ratio (for G12w calculation)
const WEEKEND_HOURS_RATIO = 2 / 7; // ~28.5% of week is weekend

/**
 * Calculate annual cost for each tariff based on consumption and pattern
 * @param annualKwh - Annual consumption in kWh
 * @param pattern - Usage pattern (day/night/even)
 * @param features - Special features selected by user
 * @param tariffPrices - OSD-specific pricing data
 * @param availableTariffs - Tariffs available for selected OSD
 */
function calculateTariffCosts(
  annualKwh: number,
  pattern: UsagePattern,
  features: SpecialFeature[],
  tariffPrices: ReturnType<typeof getOSDPrices>,
  availableTariffs: TariffType[]
): Record<TariffType, number> {
  const { dayRatio, nightRatio } = PATTERN_DISTRIBUTIONS[pattern];

  // Adjust ratios based on features
  let adjustedDayRatio = dayRatio;
  let adjustedNightRatio = nightRatio;
  let weekendRatio = WEEKEND_HOURS_RATIO;
  let solarRatio = 0.25; // Default 25% during solar hours (10-15)

  // Weekend feature increases weekend consumption
  if (features.includes('weekend')) {
    weekendRatio = 0.60; // 60% consumption on weekends
  }

  // Solar panels shift consumption to daytime
  if (features.includes('solar')) {
    solarRatio = 0.40; // 40% during solar production hours
  }

  // Heat pump increases night consumption (thermal storage)
  if (features.includes('heatPump')) {
    adjustedNightRatio = Math.min(0.80, nightRatio + 0.20);
    adjustedDayRatio = 1 - adjustedNightRatio;
  }

  // EV charging typically at night
  if (features.includes('ev')) {
    adjustedNightRatio = Math.min(0.75, nightRatio + 0.15);
    adjustedDayRatio = 1 - adjustedNightRatio;
  }

  // G11: Flat rate
  const g11Cost = annualKwh * tariffPrices.G11.flat;

  // G12: Day/Night split
  const g12Cost = annualKwh * (
    adjustedDayRatio * tariffPrices.G12.day +
    adjustedNightRatio * tariffPrices.G12.night
  );

  // G12as: Anti-smog (better night rates for heating)
  const g12asCost = annualKwh * (
    adjustedDayRatio * tariffPrices.G12as.day +
    adjustedNightRatio * tariffPrices.G12as.night
  );

  // G12w: Weekend tariff
  const g12wCost = annualKwh * (
    (1 - weekendRatio) * tariffPrices.G12w.weekday +
    weekendRatio * tariffPrices.G12w.weekend
  );

  // G13: Three-zone (peak morning/evening, off-peak afternoon, night)
  // Simplified: 30% peak, 30% off-peak, 40% night for night pattern
  const peakRatio = pattern === 'day' ? 0.50 : pattern === 'even' ? 0.35 : 0.20;
  const offpeakRatio = 0.30;
  const g13NightRatio = 1 - peakRatio - offpeakRatio;
  const g13Cost = annualKwh * (
    peakRatio * tariffPrices.G13.peak +
    offpeakRatio * tariffPrices.G13.offpeak +
    g13NightRatio * tariffPrices.G13.night
  );

  // G13s: Solar-optimized (cheap 10-15, expensive rest)
  const g13sCost = annualKwh * (
    solarRatio * tariffPrices.G13s.solar +
    (1 - solarRatio) * tariffPrices.G13s.other
  );

  // Build result object with only available tariffs (set unavailable to Infinity)
  const allCosts: Record<TariffType, number> = {
    G11: availableTariffs.includes('G11') ? Math.round(g11Cost) : Infinity,
    G12: availableTariffs.includes('G12') ? Math.round(g12Cost) : Infinity,
    G12as: availableTariffs.includes('G12as') ? Math.round(g12asCost) : Infinity,
    G12w: availableTariffs.includes('G12w') ? Math.round(g12wCost) : Infinity,
    G13: availableTariffs.includes('G13') ? Math.round(g13Cost) : Infinity,
    G13s: availableTariffs.includes('G13s') ? Math.round(g13sCost) : Infinity,
  };

  return allCosts;
}

/**
 * Get recommendation based on pattern and features
 * @param pattern - Usage pattern
 * @param features - Special features
 * @param costs - Calculated costs per tariff (Infinity for unavailable)
 * @param currentCost - Current annual cost (baseline)
 * @param availableTariffs - Tariffs available for selected OSD
 */
function getRecommendation(
  pattern: UsagePattern,
  features: SpecialFeature[],
  costs: Record<TariffType, number>,
  currentCost: number,
  availableTariffs: TariffType[]
): QuickResult {
  let primary: TariffType;
  let alternative: TariffType | undefined;
  let alternativeCondition: string | undefined;

  const hasSolar = features.includes('solar');
  const hasHeatPump = features.includes('heatPump');
  const hasEV = features.includes('ev');
  const hasWeekend = features.includes('weekend');
  const hasFlexible = features.includes('flexible');

  // Helper to check if tariff is available
  const isAvailable = (tariff: TariffType) => availableTariffs.includes(tariff);

  // Helper to get first available from list
  const getFirstAvailable = (tariffs: TariffType[]): TariffType | undefined =>
    tariffs.find(isAvailable);

  // Priority-based recommendation with availability fallback
  if (hasSolar && isAvailable('G13s')) {
    // Solar panels → G13s (prosument-optimized)
    primary = 'G13s';
    alternative = getFirstAvailable(['G12', 'G11']);
    alternativeCondition = 'jeśli instalacja PV < 5 kWp';
  } else if (hasHeatPump && isAvailable('G12as')) {
    // Heat pump → G12as (anti-smog, best night rates)
    primary = 'G12as';
    alternative = getFirstAvailable(['G12', 'G11']);
    alternativeCondition = 'jeśli ogrzewasz głównie w dzień';
  } else if (hasHeatPump && !isAvailable('G12as')) {
    // Heat pump but G12as not available → G12 with note
    primary = isAvailable('G12') ? 'G12' : 'G11';
    alternative = getFirstAvailable(['G12w', 'G11']);
    alternativeCondition = 'taryfa antysmogowa niedostępna u tego OSD';
  } else if (hasWeekend && isAvailable('G12w')) {
    // Weekend house → G12w
    primary = 'G12w';
    alternative = getFirstAvailable(['G12', 'G11']);
    alternativeCondition = 'jeśli bywasz też w tygodniu';
  } else if (hasFlexible && !hasEV && isAvailable('G13')) {
    // Flexible alone → G13 (three-zone, can optimize)
    primary = 'G13';
    alternative = getFirstAvailable(['G12', 'G11']);
    alternativeCondition = 'jeśli nie masz automatyzacji zużycia';
  } else if (hasEV) {
    // EV (with or without flexible) → G12 (simple night charging)
    primary = isAvailable('G12') ? 'G12' : 'G11';
    alternative = getFirstAvailable(['G11', 'G12w']);
    alternativeCondition = 'jeśli ładujesz też w dzień';
  } else {
    // No special features - decide based on pattern
    switch (pattern) {
      case 'day':
        primary = 'G11';
        alternative = getFirstAvailable(['G12', 'G12w']);
        alternativeCondition = 'jeśli zużycie nocne > 35%';
        break;
      case 'night':
        primary = isAvailable('G12') ? 'G12' : 'G11';
        alternative = getFirstAvailable(['G13', 'G12w', 'G11']);
        alternativeCondition = 'jeśli masz elastyczność godzinową';
        break;
      case 'even':
      default:
        primary = isAvailable('G12') ? 'G12' : 'G11';
        alternative = getFirstAvailable(['G11', 'G12w']);
        alternativeCondition = 'jeśli zużycie dzienne > 55%';
        break;
    }
  }

  // Calculate savings
  const recommendedCost = costs[primary];

  // Calculate alternative savings
  let alternativeCost: number | undefined;
  let alternativeSavings: number | undefined;
  let alternativeSavingsPercent: number | undefined;

  if (alternative) {
    alternativeCost = costs[alternative];
    alternativeSavings = currentCost - alternativeCost;
    alternativeSavingsPercent = (alternativeSavings / currentCost) * 100;
  }

  // Check if primary is actually the best - if not, swap
  // Filter out unavailable tariffs (those with Infinity cost)
  const allCosts = Object.entries(costs)
    .filter(([, cost]) => cost !== Infinity) as [TariffType, number][];
  const sortedCosts = allCosts.sort((a, b) => a[1] - b[1]);
  const cheapest = sortedCosts[0]?.[0];

  // If our primary recommendation is not the cheapest, and cheapest saves significantly more
  if (cheapest && cheapest !== primary && costs[cheapest] < recommendedCost * 0.95) {
    // Keep primary as alternative, use cheapest as primary
    alternativeCondition = `lub ${primary} ${alternativeCondition || ''}`.trim();
    alternative = primary;
    primary = cheapest;
  }

  return {
    recommendedTariff: primary,
    alternativeTariff: alternative,
    alternativeCondition,
    currentCost: Math.round(currentCost),
    recommendedCost: costs[primary],
    alternativeCost,
    savings: Math.round(Math.max(0, currentCost - costs[primary])),
    savingsPercent: Math.round(Math.max(0, ((currentCost - costs[primary]) / currentCost) * 100)),
    alternativeSavings: alternativeSavings ? Math.round(Math.max(0, alternativeSavings)) : undefined,
    alternativeSavingsPercent: alternativeSavingsPercent ? Math.round(Math.max(0, alternativeSavingsPercent)) : undefined
  };
}

/**
 * Main calculation function
 * @param monthlyBill - Monthly electricity bill in PLN
 * @param pattern - Usage pattern
 * @param features - Special features
 * @param tariffPrices - OSD-specific pricing data
 * @param availableTariffs - Tariffs available for selected OSD
 */
function calculateQuickResult(
  monthlyBill: number,
  pattern: UsagePattern,
  features: SpecialFeature[],
  tariffPrices: ReturnType<typeof getOSDPrices>,
  availableTariffs: TariffType[]
): QuickResult {
  // Estimate monthly consumption from bill (assuming G11)
  const estimatedMonthlyKwh = monthlyBill / tariffPrices.G11.flat;
  const annualKwh = estimatedMonthlyKwh * 12;
  const currentCost = monthlyBill * 12; // Baseline G11 cost

  // Calculate costs for all tariffs
  const costs = calculateTariffCosts(annualKwh, pattern, features, tariffPrices, availableTariffs);

  // Get recommendation
  return getRecommendation(pattern, features, costs, currentCost, availableTariffs);
}

// Feature configuration
const FEATURES_CONFIG: { value: SpecialFeature; label: string; icon: typeof IconFlame; description: string }[] = [
  { value: 'heatPump', label: 'Pompa ciepła / ogrzewanie elektryczne', icon: IconFlame, description: 'Piec akumulacyjny, grzejniki elektryczne' },
  { value: 'solar', label: 'Panele fotowoltaiczne', icon: IconSolarPanel, description: 'Prosument, instalacja PV' },
  { value: 'ev', label: 'Samochód elektryczny', icon: IconCar, description: 'Ładowanie w domu' },
  { value: 'weekend', label: 'Głównie weekendy', icon: IconCalendarWeek, description: 'Dom letniskowy, działka' },
  { value: 'flexible', label: 'Elastyczne zużycie', icon: IconBolt, description: 'Smart home, magazyn energii' }
];

export function QuickCalculator() {
  const [monthlyBill, setMonthlyBill] = useState(250);
  const [usagePattern, setUsagePattern] = useState<UsagePattern>('even');
  const [specialFeatures, setSpecialFeatures] = useState<SpecialFeature[]>([]);

  // Get OSD from context
  const { currentOSD, getOSDPath } = useOSD();

  // OSD pricing hook - initialize with context OSD
  const { selectedOSD, setSelectedOSD, availableTariffs, getPricing, osdMetadata } = useOSDPricing();

  // Sync selectedOSD with currentOSD from context on mount or when currentOSD changes
  useEffect(() => {
    if (currentOSD && currentOSD !== selectedOSD) {
      setSelectedOSD(currentOSD);
    }
  }, [currentOSD, selectedOSD, setSelectedOSD]);

  // Convert availableTariffs to TariffType array (filter out unsupported)
  const supportedTariffs = useMemo(() => {
    const supported: TariffType[] = ['G11', 'G12', 'G12as', 'G12w', 'G13', 'G13s'];
    return availableTariffs.filter((t) => supported.includes(t as TariffType)) as TariffType[];
  }, [availableTariffs]);

  // Get OSD-specific pricing
  const tariffPrices = useMemo(() => getOSDPrices(getPricing), [getPricing]);

  const result = useMemo(
    () => calculateQuickResult(monthlyBill, usagePattern, specialFeatures, tariffPrices, supportedTariffs),
    [monthlyBill, usagePattern, specialFeatures, tariffPrices, supportedTariffs]
  );

  // OSD select options
  const osdSelectData = useMemo(
    () =>
      OSD_CODES.map((code) => ({
        value: code,
        label: OSD_METADATA[code].shortName,
      })),
    []
  );

  const patternDescriptions: Record<UsagePattern, string> = {
    day: 'Praca z domu, dzieci w domu w ciągu dnia',
    even: 'Standardowe użytkowanie, zrównoważone',
    night: 'Główne zużycie wieczorem i w nocy'
  };

  const handleFeatureToggle = (feature: SpecialFeature) => {
    setSpecialFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  return (
    <Paper shadow="md" radius="lg" p="xl" withBorder>
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Badge color="ecoGreen" variant="light" size="sm" mb="xs">
            Szybka analiza
          </Badge>
          <Title order={3}>Sprawdź w 30 sekund</Title>
          <Text c="dimmed" size="sm">
            Odpowiedz na kilka pytań i zobacz, która taryfa jest dla Ciebie najlepsza
          </Text>
        </div>

        <Divider />

        {/* Step 0: OSD - Read-only when from context, selector otherwise */}
        <div>
          <Text fw={500} mb="xs">Twój operator sieci dystrybucyjnej (OSD)</Text>
          {currentOSD ? (
            // OSD from URL context - show read-only indicator
            <Paper p="sm" withBorder radius="sm" bg="gray.0">
              <Group gap="md" justify="space-between">
                <Group gap="xs">
                  <Box
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: osdMetadata.color,
                    }}
                  />
                  <Text fw={500}>{osdMetadata.name}</Text>
                </Group>
                <Tooltip
                  label={`Obszar: ${osdMetadata.coverage.slice(0, 2).join(', ')}${osdMetadata.coverage.length > 2 ? '...' : ''}`}
                  withArrow
                >
                  <IconMapPin size={16} style={{ opacity: 0.5 }} />
                </Tooltip>
              </Group>
            </Paper>
          ) : (
            // No OSD from context - show selector
            <Select
              value={selectedOSD}
              onChange={(value) => value && setSelectedOSD(value as OSDCode)}
              data={osdSelectData}
              leftSection={
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: osdMetadata.color,
                  }}
                />
              }
              rightSection={
                <Tooltip
                  label={`Obszar: ${osdMetadata.coverage.slice(0, 2).join(', ')}${osdMetadata.coverage.length > 2 ? '...' : ''}`}
                  withArrow
                >
                  <IconMapPin size={16} style={{ opacity: 0.5 }} />
                </Tooltip>
              }
              size="md"
              styles={{
                input: {
                  fontWeight: 500,
                },
              }}
            />
          )}
          <Text size="xs" c="dimmed" mt={4}>
            {supportedTariffs.length} taryf dostępnych • Znajdziesz go na fakturze za prąd
          </Text>
        </div>

        <Divider />

        {/* Step 1: Monthly bill */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={500}>1. Ile płacisz miesięcznie za prąd?</Text>
            <Badge size="lg" variant="filled" color="ecoGreen">
              {monthlyBill} zł
            </Badge>
          </Group>
          <Slider
            value={monthlyBill}
            onChange={setMonthlyBill}
            min={50}
            max={800}
            step={10}
            marks={[
              { value: 100, label: '100 zł' },
              { value: 300, label: '300 zł' },
              { value: 500, label: '500 zł' },
              { value: 800, label: '800 zł' }
            ]}
            color="ecoGreen"
            size="lg"
            mb="md"
          />
        </div>

        {/* Step 2: Usage pattern */}
        <div>
          <Text fw={500} mb="xs">2. Kiedy głównie zużywasz prąd?</Text>
          <SegmentedControl
            value={usagePattern}
            onChange={(value) => setUsagePattern(value as UsagePattern)}
            fullWidth
            size="md"
            data={[
              {
                value: 'day',
                label: (
                  <Group gap="xs" wrap="nowrap">
                    <IconSun size={16} />
                    <span>Dzień</span>
                  </Group>
                )
              },
              {
                value: 'even',
                label: (
                  <Group gap="xs" wrap="nowrap">
                    <IconClock size={16} />
                    <span>Równomiernie</span>
                  </Group>
                )
              },
              {
                value: 'night',
                label: (
                  <Group gap="xs" wrap="nowrap">
                    <IconMoon size={16} />
                    <span>Noc</span>
                  </Group>
                )
              }
            ]}
            color="ecoGreen"
          />
          <Text size="xs" c="dimmed" mt="xs" ta="center">
            {patternDescriptions[usagePattern]}
          </Text>
        </div>

        {/* Step 3: Special features (multi-select) */}
        <div>
          <Text fw={500} mb="xs">3. Co jeszcze Cię dotyczy? <Text span c="dimmed" size="sm">(opcjonalne)</Text></Text>
          <Stack gap="xs">
            {FEATURES_CONFIG.map(({ value, label, icon: Icon, description }) => (
              <Checkbox
                key={value}
                checked={specialFeatures.includes(value)}
                onChange={() => handleFeatureToggle(value)}
                label={
                  <Group gap="xs">
                    <Icon size={16} />
                    <div>
                      <Text size="sm">{label}</Text>
                      <Text size="xs" c="dimmed">{description}</Text>
                    </div>
                  </Group>
                }
                styles={{
                  root: {
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: specialFeatures.includes(value) ? 'var(--mantine-color-ecoGreen-0)' : 'transparent',
                    border: '1px solid',
                    borderColor: specialFeatures.includes(value) ? 'var(--mantine-color-ecoGreen-3)' : 'var(--mantine-color-gray-3)',
                    cursor: 'pointer'
                  },
                  body: { alignItems: 'flex-start' },
                  labelWrapper: { width: '100%' }
                }}
              />
            ))}
          </Stack>
        </div>

        <Divider />

        {/* Result */}
        <Card bg="ecoGreen.0" radius="md" p="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Text size="sm" c="dimmed">Rekomendowana taryfa</Text>
              <Group gap="xs" align="center">
                <Title order={2} c="ecoGreen.7">{result.recommendedTariff}</Title>
                {result.savings > 0 && (
                  <ThemeIcon size="sm" color="green" variant="light" radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                )}
              </Group>
            </div>
            <Stack ta="right" gap={0}>
              <Text size="sm" c="dimmed">Potencjalna oszczędność</Text>
              <Group gap="xs" justify="flex-end" align="baseline">
                <Title order={2} c={result.savings > 0 ? 'green.7' : 'gray.6'}>
                  {result.savings > 0 ? `${result.savings} zł` : '0 zł'}
                </Title>
                <Text size="sm" c="dimmed">/rok</Text>
              </Group>
            </Stack>
          </Group>

          {result.savings > 0 && (
            <Box mt="md">
              <Group justify="space-between" mb={4}>
                <Text size="xs" c="dimmed">Oszczędność vs G11</Text>
                <Text size="xs" fw={500} c="green.7">{result.savingsPercent}%</Text>
              </Group>
              <Progress
                value={Math.min(result.savingsPercent, 100)}
                color="green"
                size="sm"
                radius="xl"
              />
            </Box>
          )}

          {result.savings === 0 && (
            <Text size="sm" c="dimmed" mt="md">
              <IconCheck size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Twoja obecna taryfa G11 jest optymalna dla tego profilu zużycia
            </Text>
          )}
        </Card>

        {/* Alternative recommendation */}
        {result.alternativeTariff && result.alternativeSavings !== undefined && result.alternativeSavings > 0 && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
            radius="md"
          >
            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="sm" fw={500}>
                  Rozważ także: {result.alternativeTariff}
                </Text>
                <Text size="xs" c="dimmed">
                  {result.alternativeCondition}
                </Text>
              </div>
              <Badge color="blue" variant="light">
                {result.alternativeSavingsPercent}% oszczędności
              </Badge>
            </Group>
          </Alert>
        )}

        {/* CTA - Multi-OSD unlocked (Task 5.8.7) */}
        {/* UX-005: Pass monthly bill data to Basic Calculator */}
        <Stack gap="xs">
          <Button
            component={Link}
            to={currentOSD ? getOSDPath('/kalkulatory/podstawowy') : '/kalkulatory/podstawowy'}
            state={{
              selectedOSD: currentOSD || selectedOSD,
              // Estimate annual consumption from monthly bill
              // Average price ~0.85 PLN/kWh including distribution
              estimatedConsumption: Math.round((monthlyBill / 0.85) * 12),
              sourceMonthlyBill: monthlyBill,
              isEstimated: true,
            }}
            size="lg"
            rightSection={<IconArrowRight size={18} />}
            fullWidth
          >
            {BUTTON_LABELS.GO_TO_BASIC} dla {osdMetadata.shortName}
          </Button>
          <Text size="xs" c="dimmed" ta="center">
            Pełna analiza wszystkich taryf {osdMetadata.shortName} z wykresami i podziałem kosztów
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
}
